import React, { useCallback, useEffect, useState } from "react";
import Shell from "@/components/Shell";
import { api } from "@/lib/api";
import { Loader2, AlertTriangle, RefreshCcw, Trash2 } from "lucide-react";

// Iter 31.38 (Feb 2026) — Incident log viewer. Surfaces every
// client-side failure auto-filed via POST /api/admin/incident-log
// (Brief SSE drops being the canonical case, but any future page
// can post here). Read-only — write paths are auto-capture only.
const PAGE_SIZE = 50;

const KIND_PILL = {
    brief_stream_dropped: { label: "Brief SSE", cls: "border-amber-400 text-amber-400" },
    fetch_504: { label: "504", cls: "border-stamp-red text-stamp-red" },
    ui_render_error: { label: "UI crash", cls: "border-stamp-red text-stamp-red" },
};

export default function Incidents() {
    const [data, setData] = useState(null);
    const [err, setErr] = useState("");
    const [busy, setBusy] = useState(false);
    const [filter, setFilter] = useState("");

    const load = useCallback(async () => {
        setBusy(true); setErr("");
        try {
            const params = { limit: PAGE_SIZE, offset: 0 };
            if (filter) params.kind = filter;
            const { data: payload } = await api.get("/admin/incident-log", { params });
            setData(payload);
        } catch (e) {
            setErr(`[HTTP ${e.response?.status || "?"}] ${e.response?.data?.detail || e.message}`);
        } finally {
            setBusy(false);
        }
    }, [filter]);

    useEffect(() => { load(); }, [load]);

    const fileManual = async () => {
        const message = window.prompt("Operator note (will land in the incident log):");
        if (!message) return;
        try {
            await api.post("/admin/incident-log", {
                kind: "manual_note",
                message,
                path: "/incidents",
                context: { source: "operator_filed" },
                user_agent: navigator.userAgent || "",
            });
            await load();
        } catch (e) {
            setErr(`[HTTP ${e.response?.status || "?"}] ${e.response?.data?.detail || e.message}`);
        }
    };

    const incidents = data?.incidents || [];

    return (
        <Shell>
            <div className="paper-card p-6" data-testid="incidents-page">
                <div className="flex items-start justify-between mb-4">
                    <div>
                        <div className="label-typed tw-prefix">Incident log · iter 31.38</div>
                        <div className="font-heading text-3xl uppercase tracking-[0.04em] text-ink">
                            What broke
                        </div>
                        <p className="font-body text-xs text-ink-soft mt-1 max-w-2xl">
                            Every client-side failure the app could detect — Brief SSE drops,
                            fetch timeouts, render errors. Auto-captured so post-mortem has the data
                            and the canon trail isn't lost to the back-button.
                        </p>
                    </div>
                    <div className="flex items-center gap-2">
                        <button
                            onClick={fileManual}
                            data-testid="incident-file-manual"
                            className="font-heading uppercase text-[10px] tracking-[0.18em] border border-ink/30 text-ink-soft px-3 py-1.5 hover:border-stamp-red hover:text-stamp-red"
                        >
                            File a note
                        </button>
                        <button
                            onClick={load}
                            disabled={busy}
                            data-testid="incident-refresh"
                            className="font-heading uppercase text-[10px] tracking-[0.18em] border border-stamp-red text-stamp-red px-3 py-1.5 hover:bg-stamp-red hover:text-paper disabled:opacity-40 flex items-center gap-2"
                        >
                            {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCcw className="w-3 h-3" />}
                            Refresh
                        </button>
                    </div>
                </div>

                <div className="flex items-center gap-2 mb-4">
                    <span className="label-typed text-[10px]">Filter:</span>
                    {[null, "brief_stream_dropped", "manual_note", "fetch_504"].map((k) => (
                        <button
                            key={k || "all"}
                            onClick={() => setFilter(k || "")}
                            data-testid={`incident-filter-${k || "all"}`}
                            className={`font-heading uppercase text-[9px] tracking-[0.18em] px-2 py-1 border ${
                                (filter || "") === (k || "")
                                    ? "border-stamp-red bg-stamp-red text-paper-light"
                                    : "border-ink/30 text-ink hover:border-stamp-red hover:text-stamp-red"
                            }`}
                        >
                            {k ? k.replace(/_/g, " ") : "all"}
                        </button>
                    ))}
                    <div className="ml-auto label-typed text-[10px]">
                        {data ? `${incidents.length} of ${data.total}` : ""}
                    </div>
                </div>

                {err && (
                    <div className="paper-card border border-stamp-red p-3 mb-4" data-testid="incidents-error">
                        <pre className="font-body text-xs text-stamp-red whitespace-pre-wrap">{err}</pre>
                    </div>
                )}

                {!data && !err && (
                    <div className="label-typed text-[11px]">
                        <Loader2 className="w-3 h-3 inline animate-spin mr-1" /> Loading…
                    </div>
                )}

                {data && incidents.length === 0 && (
                    <div className="font-body text-sm text-ink-soft italic">
                        Quiet on the wire — no incidents on file. Either everything's working
                        or no one's looking. (Hopefully the former.)
                    </div>
                )}

                <div className="space-y-2" data-testid="incidents-list">
                    {incidents.map((i) => {
                        const pill = KIND_PILL[i.kind] || { label: i.kind, cls: "border-ink/30 text-ink" };
                        return (
                            <div
                                key={i.id}
                                className="paper-card p-3 bg-paper-dark/40"
                                data-testid={`incident-row-${i.id}`}
                            >
                                <div className="flex items-start justify-between gap-3">
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center flex-wrap gap-2 mb-1">
                                            <span className={`inline-flex items-center gap-1 border px-1.5 py-0.5 font-heading uppercase text-[9px] tracking-[0.18em] ${pill.cls}`}>
                                                <AlertTriangle className="w-3 h-3" />
                                                {pill.label}
                                            </span>
                                            <span className="label-typed text-[9px]">
                                                {(i.filed_at || "").replace("T", " ").slice(0, 19)}
                                            </span>
                                            {i.path && (
                                                <span className="font-mono text-[10px] text-ink-soft">{i.path}</span>
                                            )}
                                        </div>
                                        <div className="font-body text-xs text-ink leading-snug">
                                            {i.message}
                                        </div>
                                        {i.context && Object.keys(i.context).length > 0 && (
                                            <pre className="mt-2 bg-paper-light/50 border border-ink/15 p-2 overflow-x-auto whitespace-pre-wrap text-[10px] text-ink-soft">
{JSON.stringify(i.context, null, 2)}
                                            </pre>
                                        )}
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </Shell>
    );
}
